import { cssMap } from '@atlaskit/css';
import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import Tick from '@atlaskit/icon/core/status-success';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { type App } from '../types';
import { removeApp } from '../utils/optimisticUpdateHelpers';
import { portalZIndexFix } from '../utils/portalZIndexFix';
import { type JSX } from 'react/jsx-runtime';

const APP_REC_FLAG_WRAPPER = 'app-rec-flag-wrapper';

/**-Title--**/

const styles = cssMap({
	titleContainer: {
		font: token('font.heading.xsmall'),
	},
});

const Title = () => (
	<Box xcss={styles.titleContainer}>
		<FormattedMessage
			id="recommendations.ad-controls.dismiss-flag.not-interested.title"
			defaultMessage="Got it, thanks for letting us know"
			description="The title for a flag which is displayed when the Not Interested button is pressed. https://hello.atlassian.net/l/cp/ssoqm2Fq"
		/>
	</Box>
);

/**--Description--**/

const Description = () => (
	<FormattedMessage
		id="recommendations.ad-controls.dismiss-flag.not-interested.subtitle"
		defaultMessage="You won’t see this here as often."
		description="The subtitle for a flag which is displayed when the Not Interested button is pressed. https://hello.atlassian.net/l/cp/ssoqm2Fq"
	/>
);

/**--Confirm Icon--*/

const ConfirmIcon = () => (
	<Tick spacing="spacious" label="Success" color={token('color.icon.success', '#36B37E')} />
);

/**-Confirm Dismiss Flag--*/

export type AdControlsFlagProps = {
	app: App;
	onDismissFlag: (app: App) => void;
};

const AdControlsFlag = ({ app, onDismissFlag }: AdControlsFlagProps) => (
	<div id={APP_REC_FLAG_WRAPPER}>
		<AutoDismissFlag
			icon={<ConfirmIcon />}
			title={<Title />}
			description={<Description />}
			id={app.ccpProductId}
			key={app.ccpProductId}
			onDismissed={() => onDismissFlag(app)}
		/>
	</div>
);

/**--Confirm Dismiss Flags--**/

type AdControlsProps = {
	dismissedApps: App[];
	onDismissFlag: (app: App) => void;
};

type UseAdControls = AdControlsProps & { showFlag: (app: App) => void };

export const useAdControlsFlags = (): UseAdControls => {
	const [dismissedApps, setApps] = useState<App[]>([]);
	return {
		dismissedApps,
		showFlag: (app: App) => {
			setApps((current) => [...current, app]);
		},
		onDismissFlag: (app: App) => {
			setApps(removeApp(app));
		},
	};
};

export const AdControlsFlags = ({ dismissedApps, onDismissFlag }: AdControlsProps): JSX.Element => {
	portalZIndexFix(APP_REC_FLAG_WRAPPER);

	return (
		<FlagGroup>
			{dismissedApps.map((app) => (
				<AdControlsFlag key={app.ccpProductId} onDismissFlag={() => onDismissFlag(app)} app={app} />
			))}
		</FlagGroup>
	);
};
